import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CloudSyncOutlined, ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import {
  dropSchemaOrphanIndexes,
  ensureAllSchemaIndexes,
  ensureSchemaIndexes,
  fetchSchemaIndexStatus,
  reloadPlatformSchema,
  type SchemaIndexEntry,
  type SchemaIndexStatusResult,
} from '../../api/schemaIndexApi';
import './SchemaIndexesPage.css';

const { Text, Paragraph } = Typography;

function storageLabel(storage: SchemaIndexEntry['storage']): string {
  return storage === 'central' ? '中心库' : '业务库';
}

function orphanNames(row: SchemaIndexEntry): string[] {
  return (row.orphan_indexes ?? [])
    .map((idx) => (typeof idx.name === 'string' ? idx.name : ''))
    .filter(Boolean);
}

export function SchemaIndexesPage() {
  const [loading, setLoading] = useState(false);
  const [ensuring, setEnsuring] = useState(false);
  const [reloadingPlatform, setReloadingPlatform] = useState(false);
  const [ensuringKey, setEnsuringKey] = useState<string | null>(null);
  const [droppingKey, setDroppingKey] = useState<string | null>(null);
  const [status, setStatus] = useState<SchemaIndexStatusResult | null>(null);
  const [keyword, setKeyword] = useState('');
  const [onlyIssues, setOnlyIssues] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchSchemaIndexStatus();
      setStatus(result);
    } catch (err) {
      message.error(err instanceof Error ? err.message : '加载索引状态失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    const entries = status?.entries ?? [];
    const kw = keyword.trim().toLowerCase();
    return entries.filter((row) => {
      const hasIssue = (row.missing_count ?? 0) > 0 || (row.orphan_count ?? 0) > 0;
      if (onlyIssues && !hasIssue) return false;
      if (!kw) return true;
      const hay = `${row.schema_key} ${row.schema_profile ?? ''} ${row.collection}`.toLowerCase();
      return hay.includes(kw);
    });
  }, [keyword, onlyIssues, status?.entries]);

  const handleEnsureAll = async () => {
    setEnsuring(true);
    try {
      const result = await ensureAllSchemaIndexes();
      if ((result.total_failed ?? 0) > 0) {
        message.warning(
          `补建完成：新建 ${result.total_created}，跳过 ${result.total_skipped}，失败 ${result.total_failed}`
        );
      } else {
        message.success(
          `补建完成：新建 ${result.total_created}，跳过 ${result.total_skipped}`
        );
      }
      await load();
    } catch (err) {
      message.error(err instanceof Error ? err.message : '全量补建索引失败');
    } finally {
      setEnsuring(false);
    }
  };

  const handleReloadPlatformSchema = async () => {
    setReloadingPlatform(true);
    try {
      const preview = await reloadPlatformSchema(true);
      const keys = preview.keys ?? [];
      const confirmed = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: '加载 Platform Schema',
          width: 560,
          content: (
            <div>
              <p>
                将从<strong>当前运行镜像</strong>的 config-store 覆盖 Mongo 中全部 platform
                定义（{preview.count} 个 key）。发新版 pydc 镜像后才需要点此按钮。
              </p>
              <pre style={{ maxHeight: 200, overflow: 'auto', fontSize: 12 }}>
                {keys.join('\n')}
              </pre>
            </div>
          ),
          okText: '确认加载',
          cancelText: '取消',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmed) return;
      const result = await reloadPlatformSchema(false);
      message.success(`Platform Schema 已加载：${result.loaded ?? result.count} 个 key`);
      await load();
    } catch (err) {
      message.error(err instanceof Error ? err.message : '加载 Platform Schema 失败');
    } finally {
      setReloadingPlatform(false);
    }
  };

  const handleEnsureRow = async (row: SchemaIndexEntry) => {
    const token = `${row.schema_key}:${row.schema_profile ?? 'central'}`;
    setEnsuringKey(token);
    try {
      const result = await ensureSchemaIndexes(row.schema_key, {
        schemaProfile: row.schema_profile ?? undefined,
        allProfiles: row.is_platform_key && row.storage === 'business' && !row.schema_profile,
      });
      const entry = result.entries?.[0];
      const failed = entry?.failed_count ?? result.total_failed ?? 0;
      const created = entry?.created_count ?? result.total_created ?? 0;
      if (failed > 0) {
        message.warning(`补建 ${row.schema_key} 失败 ${failed} 项`);
      } else if (created > 0) {
        message.success(`已新建 ${created} 个索引`);
      } else {
        message.info('索引已存在，无需补建');
      }
      await load();
    } catch (err) {
      message.error(err instanceof Error ? err.message : '补建索引失败');
    } finally {
      setEnsuringKey(null);
    }
  };

  const handleDropOrphans = async (row: SchemaIndexEntry) => {
    const names = orphanNames(row);
    const confirmed = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: `删除 ${row.schema_key} 的多余索引`,
        width: 520,
        okText: '确认删除',
        okButtonProps: { danger: true },
        cancelText: '取消',
        content: (
          <div>
            <p>
              将删除下列 orphan 索引（Schema 已无对应定义）。此操作不可自动回滚，请确认不是人工运维索引。
            </p>
            <p>
              集合：<Text code>{row.collection}</Text>
              {row.schema_profile ? (
                <>
                  {' '}
                  · profile：<Text code>{row.schema_profile}</Text>
                </>
              ) : null}
            </p>
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              {names.map((name) => (
                <li key={name}>
                  <Text code>{name}</Text>
                </li>
              ))}
            </ul>
          </div>
        ),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    const token = `${row.schema_key}:${row.schema_profile ?? 'central'}`;
    setDroppingKey(token);
    try {
      if (row.storage === 'business' && !row.schema_profile) {
        message.error('业务库删除多余索引必须指定 schemaProfile');
        return;
      }
      const result = await dropSchemaOrphanIndexes(row.schema_key, {
        schemaProfile: row.schema_profile ?? undefined,
      });
      const dropped = result.dropped_count ?? 0;
      const failed = result.failed_count ?? 0;
      if (failed > 0) {
        message.warning(`已删除 ${dropped} 个，失败 ${failed} 个`);
      } else if (dropped > 0) {
        message.success(`已删除 ${dropped} 个多余索引`);
      } else {
        message.info('没有可删除的多余索引');
      }
      await load();
    } catch (err) {
      message.error(err instanceof Error ? err.message : '删除多余索引失败');
    } finally {
      setDroppingKey(null);
    }
  };

  const columns: ColumnsType<SchemaIndexEntry> = [
    {
      title: 'Schema Key',
      dataIndex: 'schema_key',
      key: 'schema_key',
      width: 180,
      render: (value: string, row) => (
        <Space size={4} wrap>
          <Text strong>{value}</Text>
          {row.is_platform_key ? <Tag color="blue">platform</Tag> : null}
        </Space>
      ),
    },
    {
      title: '业务库 / Profile',
      dataIndex: 'schema_profile',
      key: 'schema_profile',
      width: 160,
      render: (value: string | null) => value ?? <Text type="secondary">中心库</Text>,
    },
    {
      title: '存储',
      dataIndex: 'storage',
      key: 'storage',
      width: 90,
      render: (value: SchemaIndexEntry['storage']) => storageLabel(value),
    },
    {
      title: '集合',
      dataIndex: 'collection',
      key: 'collection',
      width: 160,
    },
    {
      title: '缺失 / 总计',
      key: 'counts',
      width: 120,
      render: (_, row) => (
        <span>
          <Text type={row.missing_count > 0 ? 'danger' : 'success'}>{row.missing_count}</Text>
          <Text type="secondary"> / {row.total_count}</Text>
        </span>
      ),
      sorter: (a, b) => a.missing_count - b.missing_count,
      defaultSortOrder: 'descend',
    },
    {
      title: '多余',
      key: 'orphan',
      width: 80,
      render: (_, row) => {
        const count = row.orphan_count ?? 0;
        return <Text type={count > 0 ? 'warning' : 'secondary'}>{count}</Text>;
      },
      sorter: (a, b) => (a.orphan_count ?? 0) - (b.orphan_count ?? 0),
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, row) => {
        const token = `${row.schema_key}:${row.schema_profile ?? 'central'}`;
        const orphanCount = row.orphan_count ?? 0;
        return (
          <Space size={4} wrap>
            <Button
              size="small"
              disabled={row.missing_count <= 0}
              loading={ensuringKey === token}
              onClick={() => void handleEnsureRow(row)}
            >
              补建
            </Button>
            <Button
              size="small"
              danger
              disabled={orphanCount <= 0}
              loading={droppingKey === token}
              onClick={() => void handleDropOrphans(row)}
            >
              删除多余
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="schema-indexes-page">
      <div className="schema-indexes-page-header">
        <div>
          <h2 className="schema-indexes-page-title">索引管理</h2>
          <Paragraph type="secondary" className="schema-indexes-page-desc">
            查看各 Schema 的缺失与多余（orphan）索引；可补建缺失，或按行删除多余索引。Platform
            业务类型会按已物化的 schema_profile 分别检查各业务库。
          </Paragraph>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
            刷新
          </Button>
          <Button
            icon={<CloudSyncOutlined />}
            loading={reloadingPlatform}
            onClick={() => void handleReloadPlatformSchema()}
          >
            加载 Platform Schema
          </Button>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={ensuring}
            onClick={() => void handleEnsureAll()}
          >
            全量补建缺失索引
          </Button>
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="说明"
        description={
          <>
            Schema 定义物化进 Mongo 后，索引才会按定义补建。发版改了 platform JSON 后，在本页点
            「加载 Platform Schema」（加载的是当前运行镜像，无需 kubectl）。Tenant profile
            部署物化时会自动尝试补齐 platform 业务类型索引。多余索引需人工确认后「删除多余」，ensure
            不会自动删除。
          </>
        }
      />

      <div className="schema-indexes-page-toolbar">
        <Input.Search
          allowClear
          placeholder="按 schema key / profile / 集合名筛选"
          style={{ maxWidth: 360 }}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <Button
          type={onlyIssues ? 'primary' : 'default'}
          onClick={() => setOnlyIssues((v) => !v)}
        >
          {onlyIssues ? '仅显示有问题' : '显示全部'}
        </Button>
        {status ? (
          <Text type="secondary">
            已物化 profile：{status.schema_profiles.length} 个；待补：
            <Text type={status.total_missing_indexes > 0 ? 'danger' : 'success'}>
              {' '}
              {status.total_missing_indexes}
            </Text>
            ；多余：
            <Text type={(status.total_orphan_indexes ?? 0) > 0 ? 'warning' : 'secondary'}>
              {' '}
              {status.total_orphan_indexes ?? 0}
            </Text>
          </Text>
        ) : null}
      </div>

      {loading && !status ? (
        <div className="schema-indexes-page-loading">
          <Spin />
        </div>
      ) : (
        <Table<SchemaIndexEntry>
          rowKey={(row) => `${row.schema_key}:${row.schema_profile ?? 'central'}`}
          columns={columns}
          dataSource={rows}
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 1000 }}
        />
      )}
    </div>
  );
}
